<template>
  <div class="backsearch-list">
    <div v-if="requests.length === 0" class="empty-state">
      <p>{{ $t('istarshine.backsearch.no_requests') }}</p>
      <p class="empty-hint">{{ $t('istarshine.backsearch.create_first') }}</p>
    </div>

    <div
      v-for="req in requests"
      :key="req.id"
      class="bs-card"
      :class="[cardClass(req), { 'bs-card-selected': selectedId === req.id }]"
      @click="$emit('select', req)"
    >
      <!-- Top row: ID + status on left, actions on right -->
      <div class="bs-card-header">
        <div class="bs-card-id">
          <span class="id-label">{{ primaryLabel(req) }}</span>
          <span class="status-badge" :class="'status-' + (req.status || 'draft')">
            {{ statusLabel(req.status) }}
          </span>
        </div>
        <div class="bs-card-actions actions" @click.stop>
          <template v-if="req.status === null">
            <div @click="$emit('edit', req)">
              <i class="far fa-pen"></i><span>{{ $t('istarshine.backsearch.edit') }}</span>
            </div>
            <div :class="{ disabled: deployDisabled }" @click="$emit('deploy', req)">
              <i class="far fa-rocket"></i><span>{{ $t('istarshine.backsearch.deploy') }}</span>
            </div>
            <div class="action-danger" @click="$emit('delete', req)">
              <i class="far fa-trash-alt"></i><span>{{ $t('istarshine.backsearch.delete') }}</span>
            </div>
          </template>
          <template v-else-if="req.status === 'errored' && Array.isArray(req.errors) && req.errors.length">
            <span class="error-icon" v-tooltip="req.errors.join(', ')">
              <i class="fas fa-exclamation-triangle"></i>
            </span>
          </template>
          <template v-else-if="req.status === 'processing' || req.status === 'queued'">
            <i class="fas fa-spinner rotate text-muted"></i>
          </template>
        </div>
      </div>

      <!-- Sub-line: providers + date -->
      <div class="bs-card-subtitle">
        <span v-if="!req.providers">{{ $t('istarshine.backsearch.all_providers') }}</span>
        <span v-else>{{ providerLabels(req.providers) }}</span>
        <span class="separator">|</span>
        {{ formatDate(req.start_date) }} – {{ formatDate(req.end_date) }}
      </div>

      <!-- Query text (full, wrapping) -->
      <div class="bs-card-query">
        <div class="query-text" :class="{ collapsed: !isExpanded(req.id) }">{{ req.query }}</div>
        <a
          v-if="req.query && req.query.length > 200"
          href="#"
          class="show-toggle"
          @click.prevent.stop="toggleExpand(req.id)"
        >{{ isExpanded(req.id) ? 'show less' : 'show more' }}</a>
      </div>

      <!-- Bottom metadata -->
      <div class="bs-card-meta">
        <div class="meta-item">
          <span class="meta-label">{{ $t('istarshine.backsearch.max_post_count') }}:</span>
          {{ numberFmt(req.max_post_count) }}
        </div>
        <div v-if="req.received_post_count !== null && req.received_post_count !== undefined" class="meta-item">
          <span class="meta-label">{{ $t('istarshine.backsearch.received_posts') }}:</span>
          {{ numberFmt(req.received_post_count) }}
        </div>
        <div v-if="req.excluded_users && req.excluded_users.length" class="meta-item">
          <span class="meta-label">{{ $t('istarshine.backsearch.excluded_users') }}:</span>
          {{ req.excluded_users.join(', ') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const numberFormat = new Intl.NumberFormat('en');

export default {
  name: 'BacksearchList',
  props: {
    requests: { type: Array, default: () => [] },
    deployDisabled: { type: Boolean, default: false },
    selectedId: { type: Number, default: null }
  },

  data() {
    return {
      expandedIds: {}
    };
  },

  methods: {
    primaryLabel(req) {
      const n = req.name !== null && req.name !== undefined && String(req.name).trim();
      if (n) return n;
      return `ID: ${req.id}`;
    },

    statusLabel(status) {
      if (!status) return this.$t('istarshine.backsearch.status_null');
      return this.$t('istarshine.backsearch.status_' + status);
    },

    providerLabels(providers) {
      if (!Array.isArray(providers)) return '';
      return providers.map(p => this.$te('istarshine.' + p) ? this.$t('istarshine.' + p) : p).join(', ');
    },

    formatDate(d) {
      if (!d) return '—';
      return String(d).substring(0, 10);
    },

    numberFmt(n) {
      return n !== null && n !== undefined ? numberFormat.format(n) : '—';
    },

    isExpanded(id) {
      return !!this.expandedIds[id];
    },

    toggleExpand(id) {
      this.$set(this.expandedIds, id, !this.expandedIds[id]);
    },

    cardClass(req) {
      return {
        'bs-card-errored': req.status === 'errored',
        'bs-card-completed': req.status === 'completed',
        'bs-card-processing': req.status === 'processing' || req.status === 'queued'
      };
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../styles/base/variables.scss";

.backsearch-list {
  margin-bottom: 1.5rem;
}

.empty-state {
  text-align: center;
  padding: 2rem 1rem;
  color: $gray-500;
  .empty-hint { font-size: 0.875rem; }
}

/* ── Card ── */
.bs-card {
  background: $white;
  border: 1px solid $gray-200;
  border-left: 3px solid $gray-300;
  border-radius: 4px;
  padding: 0.85rem 1rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  }
}

.bs-card-selected {
  border-left-color: $gray-300;
  background: $gray-100;
}

.bs-card-errored { border-left-color: $red; }
.bs-card-completed { border-left-color: #10b981; }
.bs-card-processing { border-left-color: #f59e0b; }

/* ── Header row ── */
.bs-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.35rem;
}

.bs-card-id {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.id-label {
  font-weight: 600;
  font-size: 0.95rem;
  color: $gray-700;
}

.bs-card-actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

/* ── Subtitle ── */
.bs-card-subtitle {
  font-size: 0.8rem;
  color: $gray-500;
  margin-bottom: 0.6rem;
}

.separator {
  margin: 0 0.35rem;
  color: $gray-300;
}

/* ── Query text ── */
.bs-card-query {
  margin-bottom: 0.6rem;
}

.query-text {
  font-size: 0.875rem;
  line-height: 1.45;
  color: $gray-700;
  white-space: pre-wrap;
  word-break: break-word;
}

.query-text.collapsed {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.show-toggle {
  font-size: 0.8rem;
  color: $gray-400;
  text-decoration: none;
  &:hover { color: $carma-teal; }
}

/* ── Bottom metadata ── */
.bs-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
  padding-top: 0.5rem;
  border-top: 1px solid $gray-100;
  font-size: 0.8rem;
  color: $gray-600;
}

.meta-label {
  font-weight: 500;
  color: $gray-500;
}

/* ── Status badge ── */
.status-badge {
  display: inline-block;
  padding: 0.1rem 0.5rem;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
}

.status-draft { background: $gray-200; color: $gray-600; }
.status-queued { background: lighten(#f59e0b, 30%); color: darken(#f59e0b, 20%); }
.status-processing { background: lighten($carma-teal, 45%); color: darken($carma-teal, 15%); }
.status-completed { background: lighten(#10b981, 40%); color: darken(#10b981, 25%); }
.status-errored { background: lighten($red, 38%); color: darken($red, 10%); }

/* ── Actions (matches boolean search / form card-header pattern) ── */
.actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  font-size: 0.8rem;

  > div {
    cursor: pointer;
    transition: background-color 0.35s ease-in;
    padding: 0.1rem 0.5rem;
    border-radius: $binary-radius * 2;

    &:hover {
      background-color: $gray-100;

      span, i { color: $carma-teal; }
    }

    &.disabled {
      pointer-events: none;
      opacity: 0.5;
    }

    &.action-danger {
      &:hover {
        span, i { color: $red; }
      }
    }
  }

  span, i {
    color: $gray-400;
    transition: color 0.25s ease-in;
  }

  span {
    font-style: italic;
    margin-bottom: 0;
    font-weight: 300;
    font-size: 0.8rem;
  }

  i {
    margin-right: 0.25rem;
    font-size: 1rem;
    transition: transform 0.2s linear;
  }
}

.error-icon {
  color: $red;
  cursor: help;
}

.text-muted { color: $gray-400; }
</style>
